import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FiBook, FiAward, FiUser, FiBarChart2, FiClock, 
  FiCheckCircle, FiXCircle, FiArrowLeft, FiPlay, FiTarget
} from 'react-icons/fi';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { getLearnerMenu } from '../../components/layout/menuConfigs';
import EducationalParticles from '../../components/common/EducationalParticles';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { showToast } from '../../components/common/Toaster';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  learnerQuizAPI,
  learnerProgressAPI,
} from '../../api/learnerEndpoints';
import { 
  DashboardContainer, 
  DashboardHeader, 
  StatsGrid, 
  StatCard, 
  StatIcon, 
  StatInfo, 
  SectionTitle,
  EmptyState,
  ContentGrid,
  ContentCard,
  ContentCardHeader,
  ContentCardBody,
  ContentCardFooter,
  ContentActions,
  ActionButton,
} from '../dashboards/Dashboard.styles';

const QuizPage = () => {
  const { quizId } = useParams();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [quizResult, setQuizResult] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [failedImages, setFailedImages] = useState(new Set());

  const menuItems = getLearnerMenu();

  useEffect(() => {
    if (quizId) {
      fetchQuiz();
    }
  }, [quizId]);

  useEffect(() => {
    if (quiz && quiz.time_limit && timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (quiz && quiz.time_limit && timeLeft === 0) {
      handleSubmitQuiz();
    }
  }, [timeLeft, quiz]);

  const fetchQuiz = async () => {
    try {
      setLoading(true);
      const response = await learnerQuizAPI.getQuiz(quizId);
      setQuiz(response.data.quiz);
      setTimeLeft(response.data.quiz.time_limit * 60); // Convert minutes to seconds
      setStartTime(Date.now());
    } catch (error) {
      showToast('error', 'Failed to load quiz');
      navigate('/learner');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, optionId) => {
    setAnswers(prev => {
      const newAnswers = {
        ...prev,
        [questionId]: optionId
      };
      console.log('Answer changed:', { questionId, optionId, totalAnswers: Object.keys(newAnswers).length, totalQuestions: quiz?.question_count });
      return newAnswers;
    });
  };

  const handleSubmitQuiz = async () => {
    if (!quiz) return;
    
    const timeTaken = startTime ? Math.floor((Date.now() - startTime) / 1000) : 0;
    const answersArray = Object.entries(answers).map(([questionId, optionId]) => ({
      question_id: questionId,
      selected_option_id: optionId
    }));

    try {
      setIsSubmitting(true);
      const response = await learnerQuizAPI.submitQuiz(quizId, answersArray, timeTaken);
      setQuizResult(response.data.result);
      setShowResults(true);
      
      // Mark quiz as completed if passed
      if (response.data.result?.passed) {
        try {
          await learnerProgressAPI.toggleCompletion('quiz', quizId, true);
          console.log('Quiz marked as completed');
        } catch (error) {
          console.error('Failed to mark quiz as completed:', error);
          // Don't show error to user as quiz submission was successful
        }
      }
    } catch (error) {
      showToast('error', 'Failed to submit quiz');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 80) return '#10B981';
    if (percentage >= 60) return '#F59E0B';
    return '#EF4444';
  };

  if (loading) {
    return (
      <DashboardLayout menuItems={menuItems}>
        <DashboardContainer>
          <LoadingSpinner size="large" text="Loading quiz..." />
        </DashboardContainer>
      </DashboardLayout>
    );
  }

  if (!quiz) {
    return (
      <DashboardLayout menuItems={menuItems}>
        <DashboardContainer>
          <EmptyState>
            <FiTarget size={48} />
            <h3>Quiz not found</h3>
            <p>The quiz you're looking for doesn't exist or has been removed</p>
            <Button onClick={() => navigate('/learner')}>
              Go to Dashboard
            </Button>
          </EmptyState>
        </DashboardContainer>
      </DashboardLayout>
    );
  }

  const allAnswered = Array.isArray(quiz?.questions)
    ? quiz.questions.every((q) => answers[q.id] !== undefined && answers[q.id] !== null && answers[q.id] !== '')
    : Object.keys(answers).length === (quiz?.question_count || 0);

  return (
    <DashboardLayout menuItems={menuItems}>
      <EducationalParticles />
      <DashboardContainer>
        <DashboardHeader>
          <div>
            <h1>{quiz.title} 🎯</h1>
            <p>{quiz.description}</p>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {quiz.time_limit && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                background: timeLeft < 60 ? '#EF444415' : '#2c5f3415',
                borderRadius: '8px',
                color: timeLeft < 60 ? '#EF4444' : '#2c5f34',
              }}>
                <FiClock />
                <span style={{ fontWeight: '600' }}>
                  {formatTime(timeLeft)}
                </span>
              </div>
            )}
            <Button 
              variant="ghost" 
              onClick={() => navigate(-1)}
            >
              <FiArrowLeft /> Back
            </Button>
          </div>
        </DashboardHeader>

        {/* Quiz Stats */}
        <StatsGrid>
          <StatCard>
            <StatIcon color="#2c5f34">
              <FiTarget />
            </StatIcon>
            <StatInfo>
              <h3>{quiz.question_count}</h3>
              <p>Questions</p>
            </StatInfo>
          </StatCard>

          <StatCard>
            <StatIcon color="#F59E0B">
              <FiClock />
            </StatIcon>
            <StatInfo>
              <h3>{quiz.time_limit || '∞'}</h3>
              <p>Minutes</p>
            </StatInfo>
          </StatCard>

          <StatCard>
            <StatIcon color="#10B981">
              <FiCheckCircle />
            </StatIcon>
            <StatInfo>
              <h3>{quiz.pass_mark}%</h3>
              <p>Pass Mark</p>
            </StatInfo>
          </StatCard>

          <StatCard>
            <StatIcon color="#8B5CF6">
              <FiBarChart2 />
            </StatIcon>
            <StatInfo>
              <h3>{quiz.total_attempts || 0}</h3>
              <p>Attempts</p>
            </StatInfo>
          </StatCard>
        </StatsGrid>

        {/* Quiz Questions */}
        {!showResults ? (
          <div>
            <SectionTitle>Questions</SectionTitle>
            {quiz.questions?.map((question, index) => {
              // Determine question type
              const questionType = question.question_type || 'text';
              const isImageQuestion = question.question_image_url || question.options?.some(opt => opt.option_image_url);
              
              return (
                <ContentCard key={question.id} style={{ marginBottom: '20px' }}>
                  <ContentCardHeader>
                    <h3>Question {index + 1}</h3>
                    <span className="badge" style={{
                      background: '#2c5f3415',
                      color: '#2c5f34',
                    }}>
                      {question.points} point{question.points !== 1 ? 's' : ''}
                    </span>
                  </ContentCardHeader>
                  <ContentCardBody>
                    {/* Question Image (if available) */}
                    {question.question_image_url && (
                      <div style={{ 
                        marginBottom: '20px', 
                        borderRadius: '8px',
                        overflow: 'hidden',
                        background: theme.colors.backgroundSecondary,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '100px'
                      }}>
                        {failedImages.has(`question_${question.id}`) ? (
                          <div style={{ 
                            padding: '20px', 
                            textAlign: 'center', 
                            color: theme.colors.textSecondary 
                          }}>
                            🖼️ Image not available
                          </div>
                        ) : (
                          <img 
                            src={question.question_image_url} 
                            alt="Question Image" 
                            style={{ 
                              maxWidth: '100%', 
                              height: 'auto',
                              display: 'block'
                            }}
                            onError={() => {
                              setFailedImages(prev => new Set([...prev, `question_${question.id}`]));
                            }}
                          />
                        )}
                      </div>
                    )}

                    {/* Question Text (if available) */}
                    {question.question_text && (
                      <p style={{ marginBottom: '20px', fontSize: '16px', lineHeight: '1.6' }}>
                        {question.question_text}
                      </p>
                    )}

                    {/* Passage Text (for passage and broken_passage types) */}
                    {question.passage_text && (
                      <div style={{
                        marginBottom: '20px',
                        padding: '16px',
                        background: theme.colors.backgroundSecondary,
                        borderLeft: '4px solid',
                        borderColor: questionType === 'broken_passage' ? '#F59E0B' : theme.colors.primary,
                        borderRadius: '8px',
                        fontSize: '15px',
                        lineHeight: '1.7',
                        color: theme.colors.textSecondary
                      }}>
                        {question.passage_text}
                      </div>
                    )}

                    {/* Options */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {question.options?.map((option) => (
                        <label
                          key={option.id}
                          style={{
                            display: 'flex',
                            alignItems: option.option_image_url ? 'flex-start' : 'center',
                            gap: '12px',
                            padding: '12px 16px',
                            border: `2px solid ${
                              answers[question.id] === option.id 
                                ? theme.colors.primary 
                                : theme.colors.border
                            }`,
                            borderRadius: '8px',
                            cursor: 'pointer',
                            background: answers[question.id] === option.id 
                              ? `${theme.colors.primary}10` 
                              : theme.colors.surface,
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <input
                            type="radio"
                            name={`question_${question.id}`}
                            value={option.id}
                            checked={answers[question.id] === option.id}
                            onChange={() => handleAnswerChange(question.id, option.id)}
                            style={{ margin: 0, marginTop: option.option_image_url ? '4px' : '0' }}
                          />
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                            {/* Option Image */}
                            {option.option_image_url && (
                              <div style={{
                                width: '100%',
                                maxWidth: '300px',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                background: theme.colors.backgroundSecondary,
                                minHeight: '60px',
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                {failedImages.has(`option_${option.id}`) ? (
                                  <div style={{ 
                                    padding: '10px', 
                                    textAlign: 'center', 
                                    color: theme.colors.textSecondary,
                                    fontSize: '24px'
                                  }}>
                                    🖼️
                                  </div>
                                ) : (
                                  <img 
                                    src={option.option_image_url} 
                                    alt={`Option ${option.order_index}`}
                                    style={{ 
                                      width: '100%', 
                                      height: 'auto',
                                      display: 'block'
                                    }}
                                    onError={() => {
                                      setFailedImages(prev => new Set([...prev, `option_${option.id}`]));
                                    }}
                                  />
                                )}
                              </div>
                            )}
                            {/* Option Text */}
                            {option.option_text && (
                              <span>{option.option_text}</span>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  </ContentCardBody>
                </ContentCard>
              );
            })}

            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center',
              marginTop: '30px',
              gap: '12px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px',
                fontSize: '14px',
                color: allAnswered ? '#10B981' : '#6B7280'
              }}>
                <FiCheckCircle />
                <span>
                  {Object.keys(answers).length} of {quiz.questions?.length || quiz.question_count} questions answered
                </span>
              </div>
              
              <Button 
                onClick={handleSubmitQuiz}
                loading={isSubmitting}
                disabled={!allAnswered || isSubmitting}
                style={{
                  opacity: !allAnswered ? 0.6 : 1
                }}
              >
                <FiCheckCircle /> Submit Quiz
              </Button>
            </div>
          </div>
        ) : (
          /* Quiz Results */
          <div>
            <SectionTitle>Quiz Results</SectionTitle>
            <ContentCard style={{
              background: `linear-gradient(135deg, ${getScoreColor(quizResult?.percentage)}15 0%, ${getScoreColor(quizResult?.percentage)}05 100%)`,
              border: `2px solid ${getScoreColor(quizResult?.percentage)}40`,
            }}>
              <ContentCardHeader>
                <div style={{ textAlign: 'center', width: '100%' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>
                    {quizResult?.passed ? '🎉' : '😔'}
                  </div>
                  <h2 style={{ 
                    margin: '0 0 8px 0',
                    color: getScoreColor(quizResult?.percentage)
                  }}>
                    {quizResult?.passed ? 'Congratulations!' : 'Keep Trying!'}
                  </h2>
                  <p style={{ margin: 0, color: theme.colors.textSecondary }}>
                    {quizResult?.passed 
                      ? 'You passed the quiz!' 
                      : `You need ${quiz.pass_mark}% to pass. Try again!`
                    }
                  </p>
                </div>
              </ContentCardHeader>
              <ContentCardBody>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '20px',
                  marginBottom: '20px',
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ 
                      fontSize: '32px', 
                      fontWeight: 'bold',
                      color: getScoreColor(quizResult?.percentage)
                    }}>
                      {quizResult?.percentage?.toFixed(1)}%
                    </div>
                    <div style={{ fontSize: '14px', color: theme.colors.textSecondary }}>
                      Final Score
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ 
                      fontSize: '32px', 
                      fontWeight: 'bold',
                      color: theme.colors.textPrimary
                    }}>
                      {quizResult?.score}/{quizResult?.total_points}
                    </div>
                    <div style={{ fontSize: '14px', color: theme.colors.textSecondary }}>
                      Points Earned
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ 
                      fontSize: '32px', 
                      fontWeight: 'bold',
                      color: theme.colors.textPrimary
                    }}>
                      {quizResult?.attempt_number}
                    </div>
                    <div style={{ fontSize: '14px', color: theme.colors.textSecondary }}>
                      Attempt Number
                    </div>
                  </div>
                </div>

                {/* Answer Review */}
                {quizResult?.graded_answers && (
                  <div>
                    <h4 style={{ margin: '0 0 16px 0' }}>Answer Review</h4>
                    {quizResult.graded_answers.map((answer, index) => {
                      const question = quiz.questions?.find(q => q.id === answer.question_id);
                      const selectedOption = question?.options?.find(o => o.id === answer.selected_option_id);
                      
                      return (
                        <div key={answer.question_id} style={{
                          padding: '12px',
                          marginBottom: '8px',
                          background: answer.is_correct ? '#10B98115' : '#EF444415',
                          border: `1px solid ${answer.is_correct ? '#10B98140' : '#EF444440'}`,
                          borderRadius: '8px',
                        }}>
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '4px'
                          }}>
                            <span style={{ fontWeight: '500' }}>
                              Question {index + 1}
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {answer.is_correct ? (
                                <FiCheckCircle color="#10B981" />
                              ) : (
                                <FiXCircle color="#EF4444" />
                              )}
                              <span style={{ 
                                fontSize: '14px',
                                color: answer.is_correct ? '#10B981' : '#EF4444'
                              }}>
                                {answer.points_earned}/{answer.points_possible} points
                              </span>
                            </div>
                          </div>
                          <div style={{ fontSize: '14px', color: theme.colors.textSecondary }}>
                            Your answer: {selectedOption?.option_text}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </ContentCardBody>
              <ContentCardFooter>
                <ContentActions>
                  <Button 
                    onClick={() => navigate(`/learner/quiz/${quizId}/review/${quizResult?.attempt_id}`)}
                    style={{
                      background: 'linear-gradient(135deg, #2c5f34 0%, #8B5CF6 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      fontSize: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <FiTarget /> Review Quiz with AI
                  </Button>
                  <ActionButton 
                    title="Retake Quiz" 
                    onClick={() => {
                      setShowResults(false);
                      setAnswers({});
                      setTimeLeft(quiz.time_limit * 60);
                      setStartTime(Date.now());
                    }}
                  >
                    <FiPlay />
                  </ActionButton>
                  <ActionButton 
                    title="Back to Course" 
                    onClick={() => navigate(-1)}
                  >
                    <FiArrowLeft />
                  </ActionButton>
                </ContentActions>
              </ContentCardFooter>
            </ContentCard>
          </div>
        )}
      </DashboardContainer>
    </DashboardLayout>
  );
};

export default QuizPage;
